import { useCallback, useEffect, useState } from "react";

import { useDebounce } from "react-use";

interface ControlledOpenStateProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const useControlledOpenState = ({
  isOpen: controlledIsOpen,
  onOpenChange,
}: ControlledOpenStateProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setOpen = useCallback(
    (nextIsOpen: boolean) => {
      if (!isControlled) {
        setInternalIsOpen(nextIsOpen);
      }

      onOpenChange?.(nextIsOpen);
    },
    [isControlled, onOpenChange],
  );

  return { isOpen, setOpen };
};

export const useDebouncedValue = <T>(value: T, delayMs: number, enabled = true) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useDebounce(() => setDebouncedValue(value), enabled ? delayMs : 0, [value, enabled]);

  return debouncedValue;
};

export const useElementWidth = (ref: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () => {
      setWidth(element.getBoundingClientRect().width);
    };

    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(() => update());
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return width;
};
